import {ArrowDownTrayIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon} from "@heroicons/react/20/solid";
import {Dialog} from "catalyst";
import {KeyboardEvent, ReactNode} from "react";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../theme/OtsTheme";
import {OtsActionButton, OtsIconActionButton} from "./OtsButton";
import {OtsFileKind, OtsFileTypeIcon, otsFileKindLabel} from "./OtsFileTypeIcon";

export type OtsMediaItem = {
  id: string;
  name: string;
  kind: OtsFileKind;
  /** Shown large for images; other kinds show their icon and name. */
  src?: string;
  caption?: ReactNode;
};

export type OtsMediaCarouselDialogProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  items: OtsMediaItem[];
  /** Index of the item shown. */
  index: number;
  onIndexChange: (index: number) => void;
  /** Adds a Download button for the item shown. */
  onDownload?: (item: OtsMediaItem) => void;
  /** Adds Download all when there is more than one item. */
  onDownloadAll?: () => void;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

/** Steps through a set of pictures and files one at a time, with a thumbnail strip, arrow keys, and downloads. */
export const OtsMediaCarouselDialog = ({
  open,
  onClose,
  title,
  items,
  index,
  onIndexChange,
  onDownload,
  onDownloadAll,
  brand,
  colorScheme,
}: OtsMediaCarouselDialogProps) => {
  const current = items[Math.min(Math.max(index, 0), items.length - 1)];
  const step = (offset: number) => onIndexChange((index + offset + items.length) % items.length);
  const onKeyDown = (event: KeyboardEvent) => {
    if (items.length < 2) return;
    if (event.key === "ArrowRight") step(1);
    else if (event.key === "ArrowLeft") step(-1);
  };

  return (
    <Dialog open={open && !!current} onClose={onClose} size="4xl" className="!bg-transparent !p-0">
      <OtsThemeRoot brand={brand} colorScheme={colorScheme} defaultColorScheme="system" className="rounded-[inherit] bg-[var(--ots-surface)]" onKeyDown={onKeyDown}>
        <header className="!flex items-center gap-3 border-b border-[var(--ots-outline-variant)] px-4 py-3">
          <div className="min-w-0 flex-1">
            <h2 className="truncate font-[family-name:var(--ots-font-display)] text-base font-semibold">{title}</h2>
            {current && <p aria-live="polite" className="truncate text-xs text-[var(--ots-text-muted)]">
              {index + 1} of {items.length} · {current.name}
            </p>}
          </div>
          {onDownload && current && <OtsActionButton size="compact" icon={<ArrowDownTrayIcon/>} onClick={() => onDownload(current)}>Download</OtsActionButton>}
          {onDownloadAll && items.length > 1 && <OtsActionButton size="compact" variant="tonal" icon={<ArrowDownTrayIcon/>} onClick={onDownloadAll}>Download all ({items.length})</OtsActionButton>}
          <OtsIconActionButton variant="text" size="compact" icon={<XMarkIcon/>} label="Close" onClick={onClose}/>
        </header>

        {current && <div className="relative flex h-[60vh] min-h-64 items-center justify-center bg-[var(--ots-background)]">
          {current.kind === "image" && current.src
            ? <img src={current.src} alt={current.name} className="max-h-full max-w-full object-contain"/>
            : <div className="flex flex-col items-center gap-3 p-6 text-center">
              <OtsFileTypeIcon kind={current.kind} className="size-20"/>
              <p className="font-semibold">{current.name}</p>
              <p className="text-sm text-[var(--ots-text-muted)]">{otsFileKindLabel(current.kind)} · no preview. Download it to open.</p>
            </div>}
          {items.length > 1 && <>
            <OtsIconActionButton variant="secondary" icon={<ChevronLeftIcon/>} label="Previous" className="!absolute left-3 !rounded-full shadow-md" onClick={() => step(-1)}/>
            <OtsIconActionButton variant="secondary" icon={<ChevronRightIcon/>} label="Next" className="!absolute right-3 !rounded-full shadow-md" onClick={() => step(1)}/>
          </>}
        </div>}

        {current?.caption != null && <p className="border-t border-[var(--ots-outline-variant)] px-4 py-2 text-sm text-[var(--ots-text-muted)]">{current.caption}</p>}

        {items.length > 1 && (
          <ul aria-label="All items" className="flex gap-2 overflow-x-auto border-t border-[var(--ots-outline-variant)] px-4 py-3">
            {items.map((item, itemIndex) => (
              <li key={item.id} className="shrink-0">
                <button
                  type="button"
                  aria-label={`Show ${item.name}`}
                  aria-current={itemIndex === index ? "true" : undefined}
                  onClick={() => onIndexChange(itemIndex)}
                  className={[
                    "flex size-14 items-center justify-center overflow-hidden rounded-md border bg-[var(--ots-surface-variant)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]",
                    itemIndex === index ? "border-[var(--ots-primary)] ring-2 ring-[var(--ots-primary)]" : "border-[var(--ots-outline-variant)] opacity-70 hover:opacity-100",
                  ].join(" ")}>
                  {item.kind === "image" && item.src
                    ? <img src={item.src} alt="" loading="lazy" className="size-full object-cover"/>
                    : <OtsFileTypeIcon kind={item.kind} className="size-7"/>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </OtsThemeRoot>
    </Dialog>
  );
};
